import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text } from '../../lib/react-native-compat';
import { selectedJobAtom, selectedVersionAtom, dataSourceVersionAtom } from '../../store/company';
import { loadUserData, loadProjectSchedule } from '../../db/company';

// Key Milestones surfaced on the report list so field staff see target dates
// without opening the forecast. Two columns side by side (wrap on phones):
// Overview-tab milestones (baseline/forecast) and milestone activities from
// the active schedule import. Renders nothing while loading or when the job
// has neither.
//
// Overview rows live in Forecast_User_Data (tab 'Overview', field
// 'Milestone') as JSON: { milestone, owner, currentBaseline, actualForecast,
// prevForecast, remarks, ... }. Older rows may use { description, targetDate }.
// Schedule rows are PC.Schedule_Activity with Is_Milestone set; their baseline
// is the activity's date in the job's first schedule import (Baseline_* from
// readScheduleActivities), falling back to the active import's date when the
// activity wasn't in that first upload.
//
// Reads the latest forecast version rather than the selected one — field staff
// never pick a version, and the report list should show current dates even
// when someone left an old version selected in the top menu.

const fmtDate = (iso) => {
    const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return '—';
    return `${m}/${d}/${String(y).padStart(4, '0').slice(2)}`;
};

// Fetches both milestone sources for the selected job. Exported so
// ReportCalendar can load once and share the data with the embedded
// JobMilestones (pass the result as its `data` prop to skip a second fetch).
export const useJobMilestones = (enabled = true) => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [milestones, setMilestones] = useState([]);
    const [scheduleMilestones, setScheduleMilestones] = useState([]);

    const latestVersion = Array.isArray(versions) && versions.length > 0
        ? versions.reduce((max, v) => (v.Version_GUID > max ? v.Version_GUID : max), 0)
        : selectedVersion;

    useEffect(() => {
        let cancelled = false;
        if (!enabled || !selectedJob || !latestVersion) {
            setMilestones([]);
            return undefined;
        }
        loadUserData({ id: selectedJob, forecastGuid: latestVersion }).then((res) => {
            if (cancelled) return;
            const rows = (res?.data || [])
                .filter((r) => r.Forecast_Tab === 'Overview' && r.Forecast_Field_Name === 'Milestone')
                .map((r) => {
                    try { return JSON.parse(r.Forecast_Value); } catch (e) { return null; }
                })
                .filter(Boolean)
                .map((m) => ({
                    name: m.milestone || m.description || '',
                    baseline: m.currentBaseline || m.targetDate || '',
                    forecast: m.actualForecast || '',
                }))
                .filter((m) => m.name || m.baseline || m.forecast)
                .sort((a, b) => (a.forecast || a.baseline || '9999').localeCompare(b.forecast || b.baseline || '9999'));
            setMilestones(rows);
        }).catch(() => { if (!cancelled) setMilestones([]); });
        return () => { cancelled = true; };
    }, [enabled, selectedJob, latestVersion]);

    useEffect(() => {
        let cancelled = false;
        if (!enabled || !selectedJob) {
            setScheduleMilestones([]);
            return undefined;
        }
        loadProjectSchedule({ buId: String(selectedJob) }).then((res) => {
            if (cancelled) return;
            const rows = (res?.activities || [])
                .filter((a) => a.Is_Milestone === true || a.Is_Milestone === 1)
                .map((a) => ({
                    name: a.Activity_Name || a.Activity_Code || '',
                    baseline: a.Baseline_Finish || a.Baseline_Start || a.Finish_Date || a.Start_Date || '',
                    forecast: a.Actual_Finish || a.Finish_Date || a.Actual_Start || a.Start_Date || '',
                    done: Number(a.Pct_Complete) >= 100,
                }))
                .filter((m) => m.name || m.forecast || m.baseline)
                .sort((a, b) => (String(a.forecast || a.baseline).slice(0, 10) || '9999')
                    .localeCompare(String(b.forecast || b.baseline).slice(0, 10) || '9999'));
            setScheduleMilestones(rows);
        }).catch(() => { if (!cancelled) setScheduleMilestones([]); });
        return () => { cancelled = true; };
    }, [enabled, selectedJob]);

    return { milestones, scheduleMilestones };
};

const JobMilestones = ({ embedded, data }) => {
    const fetched = useJobMilestones(!data);
    const { milestones, scheduleMilestones } = data || fetched;

    if (milestones.length === 0 && scheduleMilestones.length === 0) return null;

    const cellDate = (value, late) => (
        <Text style={{
            width: 64, fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
            color: late ? '#DC2626' : '#334155', fontWeight: late ? '700' : '400',
        }}
        >
            {fmtDate(value)}
        </Text>
    );

    const headerCell = (label, first) => (
        <Text style={{
            flex: first ? 1 : undefined, width: first ? undefined : 64,
            fontSize: 10, fontWeight: '600', color: '#94A3B8', textAlign: first ? 'left' : 'right',
        }}
        >
            {label}
        </Text>
    );

    const columnTitle = (label) => (
        <Text style={{ fontSize: 11, fontWeight: '700', color: '#64748B', marginBottom: 6 }}>{label}</Text>
    );

    const overviewColumn = milestones.length > 0 && (
        <View style={{ flex: 1, minWidth: 280 }}>
            {scheduleMilestones.length > 0 && columnTitle('Overview')}
            <View style={{ flexDirection: 'row', columnGap: 12, marginBottom: 4 }}>
                {headerCell('MILESTONE', true)}
                {headerCell('BASELINE')}
                {headerCell('FORECAST')}
            </View>
            {milestones.map((m, i) => {
                const late = m.baseline && m.forecast && m.forecast.slice(0, 10) > m.baseline.slice(0, 10);
                return (
                    <View
                        key={i}
                        style={{
                            flexDirection: 'row', alignItems: 'center', columnGap: 12,
                            paddingTop: 5, paddingBottom: 5,
                            borderTop: i > 0 ? '1px solid #F1F5F9' : 'none',
                        }}
                    >
                        <Text style={{ flex: 1, fontSize: 12, fontWeight: '600', color: '#0F172A' }}>{m.name || '—'}</Text>
                        {cellDate(m.baseline, false)}
                        {cellDate(m.forecast, late)}
                    </View>
                );
            })}
        </View>
    );

    const scheduleColumn = scheduleMilestones.length > 0 && (
        <View style={{ flex: 1, minWidth: 280 }}>
            {milestones.length > 0 && columnTitle('Schedule')}
            <View style={{ flexDirection: 'row', columnGap: 12, marginBottom: 4 }}>
                {headerCell('MILESTONE', true)}
                {headerCell('BASELINE')}
                {headerCell('FORECAST')}
            </View>
            {scheduleMilestones.map((m, i) => {
                const late = m.baseline && m.forecast
                    && String(m.forecast).slice(0, 10) > String(m.baseline).slice(0, 10);
                return (
                    <View
                        key={i}
                        style={{
                            flexDirection: 'row', alignItems: 'center', columnGap: 12,
                            paddingTop: 5, paddingBottom: 5,
                            borderTop: i > 0 ? '1px solid #F1F5F9' : 'none',
                        }}
                    >
                        <Text style={{ flex: 1, fontSize: 12, fontWeight: m.done ? '400' : '600', color: m.done ? '#64748B' : '#0F172A' }}>
                            {m.name || '—'}
                        </Text>
                        {cellDate(m.baseline, false)}
                        {cellDate(m.forecast, late)}
                    </View>
                );
            })}
        </View>
    );

    return (
        <View style={embedded
            ? { borderBottom: '1px solid #F1F5F9', paddingBottom: 12, marginBottom: 12 }
            : { borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 14, marginBottom: 18 }}
        >
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginBottom: 8 }}>Key Milestones</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 24, rowGap: 12, alignItems: 'flex-start' }}>
                {overviewColumn}
                {scheduleColumn}
            </View>
        </View>
    );
};

export default JobMilestones;
